import React from 'react';
import BulkUpdateDialog from '@components-clean/core/bulk-update-dialog/bulk-update-dialog';
import { SelectedFieldValuesProps } from '@interfaces-clean/bulk-update.interface';
import { SelectedRowsProps } from '@interfaces-clean/observations.interface';
import { SubjectObservationBulkUpdateDto } from '@interfaces-clean/subject-monitoring/subject-observations';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromLocal } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import SubjectObservationsApi from '@apis/bff-api/subject-monitoring/subject-observations';

interface BulkEditSmObservationDialogProps {
    studyRef: string;
    selectedRows: SelectedRowsProps[];
    onClose: () => void;
    onSubmit: () => void;
}

const SM_OBSERVATION_FIELDS = [
    { field: 'closedDate', type: 'date', required: false, textLimit: 0 },
    { field: 'comments', type: 'text', required: false, textLimit: 3000 },
    { field: 'ownerRef', type: 'smOwnerLov', required: false, textLimit: 0 },
    { field: 'rootCause', type: 'text', required: false, textLimit: 3000 },
];

const BulkEditSmObservationDialog = ({
    studyRef,
    selectedRows,
    onClose,
    onSubmit,
}: BulkEditSmObservationDialogProps) => {
    const { t } = useTranslation(['common']);
    const { t: tSm } = useTranslation('sm', { keyPrefix: 'monitoringToolkit.bulkEditObservationDialog' });
    const { trigger: bulkUpdate } = SubjectObservationsApi.useBulkUpdate(studyRef);

    const warningMessage = tSm('warningMessage', { count: selectedRows.length });

    const buildDto = (submittedValues: SelectedFieldValuesProps[]): SubjectObservationBulkUpdateDto => {
        const closedDateField = submittedValues.find((f) => f.field === 'closedDate');
        const commentsField = submittedValues.find((f) => f.field === 'comments');
        const ownerRefField = submittedValues.find((f) => f.field === 'ownerRef');
        const rootCauseField = submittedValues.find((f) => f.field === 'rootCause');

        const closedDateValue = closedDateField?.value as string | undefined;
        const hasClosedDate = !!closedDateValue && closedDateValue !== '';

        return {
            observationIds: selectedRows.map((r) => r.id),
            closedDate: hasClosedDate ? (stringDateWithoutTimeFromLocal(closedDateValue!) ?? closedDateValue!) : null,
            clearClosedDate: closedDateField != null && !hasClosedDate,
            comments: commentsField != null ? ((commentsField.value as string) ?? null) : null,
            ownerRef: ownerRefField != null ? (ownerRefField.value as string) || null : null,
            rootCause: rootCauseField != null ? ((rootCauseField.value as string) ?? null) : null,
        };
    };

    const onSubmitHandler = async (submittedValues: SelectedFieldValuesProps[]) => {
        try {
            const success = await bulkUpdate(buildDto(submittedValues));
            if (success) {
                ShowSuccess(t('actionsSuccessfulToast'));
                onSubmit();
            } else {
                ShowError(t('common:errorMessage'));
            }
        } catch {
            ShowError(t('common:errorMessage'));
        }
    };

    return (
        <BulkUpdateDialog
            studyRef={studyRef}
            dialogTitle={tSm('title')}
            warningMessage={warningMessage}
            fields={SM_OBSERVATION_FIELDS}
            onClose={onClose}
            onSubmit={onSubmitHandler}
            unsavedChangesArea="subject-monitoring-toolkit"
        />
    );
};

export default BulkEditSmObservationDialog;
